import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { BookOpen, Edit3, Save, RotateCcw, Eye, FileText, Globe, Copy, Check, Code } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './ManualEditorStyles.css';
import './ManualContentStyles.css';
import { useTranslation } from 'react-i18next';

interface ManualContent {
  id: number;
  title: string;
  content: string;
  version: string;
  language: string;
  created_at: string;
  updated_at: string;
  updated_by_name?: string;
}


// Removed better-table registration due to compatibility issues

export function ManualEditorTab() {
  const { t } = useTranslation('common');
  const [manual, setManual] = useState<ManualContent | null>(null);
  const [, setAllManuals] = useState<ManualContent[]>([]);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('cs');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [version, setVersion] = useState('');

  // Load manual content for selected language
  const loadManual = async (language: string = selectedLanguage) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch(`/api/admin/manual?language=${language}`);
      if (!response.ok) {
        throw new Error(t('manual_editor.load_error'));
      }
      
      const data = await response.json();
      if (data.success && data.data) {
        setManual(data.data);
        setTitle(data.data.title);
        setContent(data.data.content);
        setVersion(data.data.version);
      } else {
        // No manual for this language, reset form
        setManual(null);
        setTitle('');
        setContent('');
        setVersion('1.0');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unexpected'));
    } finally {
      setIsLoading(false);
    }
  };

  // Load all manuals
  const loadAllManuals = async () => {
    try {
      const response = await fetch('/api/admin/manual/all');
      if (!response.ok) {
        throw new Error(t('manual_editor.load_error'));
      }
      
      const data = await response.json();
      if (data.success) {
        setAllManuals(data.data || []);
      }
    } catch (err) {
      console.error('Error loading all manuals:', err);
    }
  };

  // Load available languages
  const loadAvailableLanguages = async () => {
    try {
      const response = await fetch('/api/admin/manual/languages');
      if (!response.ok) {
        throw new Error(t('manual_editor.load_error'));
      }
      
      const data = await response.json();
      if (data.success) {
        setAvailableLanguages(data.data || []);
      }
    } catch (err) {
      console.error('Error loading available languages:', err);
    }
  };


  // Save manual content
  const saveManual = async () => {
    if (!title.trim() || !content.trim() || !version.trim()) {
      setError(t('manual_editor.required_fields'));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const method = manual ? 'PUT' : 'POST';
      const response = await fetch('/api/admin/manual', {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: title.trim(),
          content: content.trim(),
          version: version.trim(),
          language: selectedLanguage,
        }),
      });

      if (!response.ok) {
        throw new Error(t('manual_editor.save_error'));
      }

      const data = await response.json();
      if (data.success) {
        setSuccess(t('manual_editor.save_success'));
        // Reload manual
        await loadManual(selectedLanguage);
        await loadAllManuals();
        await loadAvailableLanguages();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manual_editor.save_error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Reset form
  const resetForm = () => {
    if (manual) {
      setTitle(manual.title);
      setContent(manual.content);
      setVersion(manual.version);
    } else {
      setTitle('');
      setContent('');
      setVersion('1.0');
    }
    setError(null);
    setSuccess(null);
  };

  // Handle language change
  const handleLanguageChange = async (newLanguage: string) => {
    setSelectedLanguage(newLanguage);
    await loadManual(newLanguage);
    setError(null);
    setSuccess(null);
  };

  // Check if form has changes or if it's a new manual with content
  const hasChanges = manual ? (
    title !== manual.title ||
    content !== manual.content ||
    version !== manual.version
  ) : (
    title.trim() !== '' || content.trim() !== '' || version.trim() !== ''
  );

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      await loadAvailableLanguages();
      await loadAllManuals();
      await loadManual(selectedLanguage);
    };
    loadData();
  }, []);


  // Copy source code to clipboard
  const copySourceCode = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };



  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          <h2 className="text-lg font-semibold">{t('manual_editor.title')}</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={resetForm}
            disabled={!manual || !hasChanges}
            className="flex items-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            {t('manual_editor.reset')}
          </Button>
          <Button
            onClick={saveManual}
            disabled={isSaving || !hasChanges}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {isSaving ? t('manual_editor.saving') : t('buttons.save')}
          </Button>
        </div>
      </div>

      {/* Language selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            {t('manual_editor.language_selection')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="language">{t('manual_editor.select_language')}</Label>
              <select
                id="language"
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 bg-white"
              >
                <option value="cs">🇨🇿 Čeština</option>
                <option value="en">🇺🇸 English</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('manual_editor.available_languages')}:</span>
              <div className="flex gap-1">
                {availableLanguages.map(lang => (
                  <Badge key={lang} variant={lang === selectedLanguage ? "default" : "secondary"}>
                    {lang === 'cs' ? '🇨🇿 CS' : '🇺🇸 EN'}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          {manual && (
            <div className="mt-4 p-3 bg-gray-50 rounded-md">
              <div className="text-sm text-gray-600">
                <strong>{t('manual_editor.current_manual')}:</strong> {manual.title} (v{manual.version})
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {t('manual_editor.last_updated')}: {new Date(manual.updated_at).toLocaleDateString()}
                {manual.updated_by_name && ` • ${t('manual_editor.by')}: ${manual.updated_by_name}`}
              </div>
            </div>
          )}
          {!manual && (
            <div className="mt-4 p-3 bg-orange-50 rounded-md">
              <div className="text-sm text-orange-800">
                {t('manual_editor.no_manual_for_language')}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <p>{error}</p>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <p className="text-green-800">{success}</p>
        </Alert>
      )}

      <Tabs defaultValue="edit" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="edit" className="flex items-center gap-2">
            <Edit3 className="h-4 w-4" />
            {t('manual_editor.tab_edit')}
          </TabsTrigger>
          <TabsTrigger value="source" className="flex items-center gap-2">
            <Code className="h-4 w-4" />
            {t('manual_editor.tab_source')}
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            {t('manual_editor.tab_preview')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="edit" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('manual_editor.basic_info')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">{t('manual_editor.manual_title')}</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('manual_editor.manual_title_placeholder')}
                  />
                </div>
                <div>
                  <Label htmlFor="version">{t('manual_editor.version')}</Label>
                  <Input
                    id="version"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder={t('manual_editor.version_placeholder')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit3 className="h-5 w-5" />
                {t('manual_editor.manual_content')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="content">{t('manual_editor.manual_content')}</Label>
                <div className="min-h-96 border rounded-md">
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    placeholder={t('manual_editor.content_placeholder')}
                    modules={{
                      toolbar: [
                        [{ 'header': '1'}, { 'header': '2'}, { 'header': '3'}, { 'font': [] }],
                        [{ 'size': ['small', false, 'large', 'huge'] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'color': [] }, { 'background': [] }],
                        [{ 'script': 'sub'}, { 'script': 'super' }],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'list': 'check' }],
                        [{ 'indent': '-1'}, { 'indent': '+1' }],
                        [{ 'direction': 'rtl' }],
                        [{ 'align': [] }],
                        ['blockquote', 'code-block'],
                        ['link', 'image', 'video'],
                        ['clean']
                      ]
                    }}
                    formats={[
                      'header', 'font', 'size',
                      'bold', 'italic', 'underline', 'strike', 'blockquote',
                      'list', 'bullet', 'check', 'indent',
                      'link', 'image', 'video',
                      'color', 'background', 'script',
                      'align', 'direction', 'code-block'
                    ]}
                    style={{
                      height: '400px',
                      marginBottom: '42px'
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('manual_editor.formatting_help')}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="source" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  {t('manual_editor.source_title')}
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copySourceCode}
                  className="flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      {t('manual_editor.copied')}
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      {t('manual_editor.copy_source')}
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {t('manual_editor.source_description')}
                </p>
                <div className="relative">
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="w-full h-96 p-4 font-mono text-sm border rounded-md resize-none bg-gray-50"
                    placeholder={t('manual_editor.source_placeholder')}
                    spellCheck={false}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('manual_editor.source_help')}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                {t('manual_editor.preview_title')}
              </CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{t('manual_editor.version')}: {version}</span>
                <Separator orientation="vertical" className="h-4" />
                <span>{title}</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-96 overflow-y-auto border rounded-md p-4">
                <div 
                  className="prose prose-sm max-w-none manual-content"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
} 